// © 2025 Ananda Kumar. All rights reserved.
// Unauthorized copying of this file, via any medium, is strictly prohibited.

import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { SKIP_LOADER } from './loader-context.token';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) { }

  getData<T = any>(endpoint: string): Observable<T>;
  getData<T = any>(endpoint: string, headers: { [key: string]: string }): Observable<T>;
  getData<T = any>(endpoint: string, headers?: { [key: string]: string }): Observable<T> {
    const options = headers ? { headers } : {};
    return this.http.get<{ success: boolean; value: T; errors?: string[] }>(`${this.baseUrl}/${endpoint}`, options).pipe(
      map(response => {
        if (!response.success) {
          throw new Error(response.errors?.join(', ') || 'Request failed');
        }
        return response.value as T;
      })
    );
  }

  getText(endpoint: string): Observable<string> {
    return this.http.get(`${this.baseUrl}/${endpoint}`, { responseType: 'text' });
  }

  postData<T = any>(endpoint: string, data?: unknown): Observable<T> {
    return this.http.post<{ success: boolean; value: T; errors?: string[] }>(`${this.baseUrl}/${endpoint}`, data).pipe(
      map(response => {
        if (!response.success) {
          throw new Error(response.errors?.join(', ') || 'Request failed');
        }
        return response.value as T;
      })
    );
  }

  postBlob(endpoint: string, body: unknown): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/${endpoint}`, body, { responseType: 'blob' });
  }

  putData<T = any>(endpoint: string, data: unknown): Observable<T> {
    return this.http.put<{ success: boolean; value: T; errors?: string[] }>(`${this.baseUrl}/${endpoint}`, data).pipe(
      map(response => {
        if (!response.success) {
          throw new Error(response.errors?.join(', ') || 'Request failed');
        }
        return response.value as T;
      })
    );
  }

  deleteData<T = any>(endpoint: string): Observable<T> {
    return this.http.delete<{ success: boolean; value: T; errors?: string[] }>(`${this.baseUrl}/${endpoint}`).pipe(
      map(response => {
        if (!response.success) {
          throw new Error(response.errors?.join(', ') || 'Request failed');
        }
        return response.value as T;
      })
    );
  }

  deleteDataWithBody<T = any>(endpoint: string, body: unknown): Observable<T> {
    return this.http.delete<{ success: boolean; value: T; errors?: string[] }>(`${this.baseUrl}/${endpoint}`, { body }).pipe(
      map(response => {
        if (!response.success) {
          throw new Error(response.errors?.join(', ') || 'Request failed');
        }
        return response.value as T;
      })
    );
  }

  backgroundGet<T = any>(endpoint: string): Observable<T> {
    return this.http.get<{ success: boolean; value: T; errors?: string[] }>(
      `${this.baseUrl}/${endpoint}`,
      { context: new HttpContext().set(SKIP_LOADER, true) }
    ).pipe(
      map(response => {
        if (!response.success) {
          throw new Error(response.errors?.join(', ') || 'Request failed');
        }
        return response.value as T;
      })
    );
  }
}
